import { useEffect, useRef, useState } from "react";

import { Bell, X } from "lucide-react";
import { Link, useLocation } from "react-router-dom";

import { useCookieConsent } from "@/hooks/useCookieConsent";
import {
  dismissWaitlistFloater,
  isPremiumWaitlistJoined,
  isWaitlistFloaterDismissed,
  PREMIUM_GATE_EVENT_NAME,
  WAITLIST_FLOATER_EVENT_NAME,
} from "@/lib/waitlist";
import { routePaths } from "@/routes/routePaths";

const PREMIUM_FEATURES = [
  "TikTok/Reels links",
  "AI Notes",
  "Higher limits",
] as const;

export function PremiumWaitlistFloater() {
  const location = useLocation();
  const { requiresConsent } = useCookieConsent();
  const [visible, setVisible] = useState(
    () => !isWaitlistFloaterDismissed() && !isPremiumWaitlistJoined(),
  );
  const [highlighted, setHighlighted] = useState(false);
  const highlightTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const asideRef = useRef<HTMLElement>(null);

  useEffect(() => {
    function syncWaitlistState() {
      setVisible(!isWaitlistFloaterDismissed() && !isPremiumWaitlistJoined());
    }

    function handlePremiumGate() {
      // Re-show the floater even if previously dismissed, then pulse it.
      setVisible(true);
      setHighlighted(true);
      if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current);
      highlightTimerRef.current = setTimeout(() => setHighlighted(false), 2400);
    }

    window.addEventListener("storage", syncWaitlistState);
    window.addEventListener(WAITLIST_FLOATER_EVENT_NAME, syncWaitlistState);
    window.addEventListener(PREMIUM_GATE_EVENT_NAME, handlePremiumGate);
    return () => {
      window.removeEventListener("storage", syncWaitlistState);
      window.removeEventListener(
        WAITLIST_FLOATER_EVENT_NAME,
        syncWaitlistState,
      );
      window.removeEventListener(PREMIUM_GATE_EVENT_NAME, handlePremiumGate);
      if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (isPremiumWaitlistJoined()) {
      setVisible(false);
    }
  }, [location.pathname]);

  if (!visible || location.pathname === routePaths.register) {
    return null;
  }

  const bottomOffset = requiresConsent ? "bottom-36" : "bottom-3";

  return (
    <aside
      ref={asideRef}
      style={
        highlighted
          ? { boxShadow: "0 0 0 2px #fff, 0 0 18px rgba(255,255,255,0.25)" }
          : undefined
      }
      className={[
        "fixed right-3 z-30 w-[min(20rem,calc(100vw-1.5rem))] border bg-neutral-950 p-3 text-white shadow-2xl transition-all duration-500",
        highlighted ? "border-white" : "border-neutral-700",
        bottomOffset,
      ].join(" ")}
    >
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center bg-white text-neutral-950">
          <Bell className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="pr-7 text-sm font-semibold">Premium tools waitlist</h2>
          <div className="mt-2 flex items-center gap-2">
            <Link
              to={routePaths.register}
              className="inline-flex h-8 shrink-0 items-center border border-neutral-500 px-3 text-sm font-medium transition hover:border-white"
            >
              Join waitlist
            </Link>
            <span className="flex min-w-0 flex-wrap gap-x-1 gap-y-0.5 text-[11px] text-neutral-400">
              {PREMIUM_FEATURES.map((feature, i) => (
                <span key={feature} className="flex items-center gap-x-1">
                  {i > 0 && <span className="text-neutral-600">·</span>}
                  {feature}
                </span>
              ))}
            </span>
          </div>
        </div>
        <button
          type="button"
          onClick={() => {
            dismissWaitlistFloater();
            setVisible(false);
          }}
          className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center border border-neutral-700 text-neutral-300 transition hover:border-white hover:text-white"
          aria-label="Dismiss premium waitlist prompt"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </aside>
  );
}
